import getGraphQLClient from '../graphQLClient';

export type ProtocolSiteSearchResult = {
  name?: string;
  siteCity?: { value?: string } | null;
  siteState?: {
    jsonValue?: { name?: string; displayName?: string } | null;
  } | null;
  siteStatus?: { value?: string } | null | string;
};

type ProtocolStudySearchResult = {
  name?: string;
  children?: {
    results?: ProtocolSiteSearchResult[];
  };
};

type ProtocolStudySearchResponse = {
  search?: {
    results?: ProtocolStudySearchResult[];
  };
};

const DEFAULT_LANGUAGE = 'en';
const DEFAULT_PROTOCOL_SITE_STATUSES =
  'Enrollment Open|Active|Not Yet Enrolling|Recruiting|Enrolling|Selected|Not yet recruiting';

const SITE_RESULT_FIELDS = `
  name
  siteCity: field(name: "city") {
    value
  }
  siteState: field(name: "state") {
    jsonValue
  }
  siteStatus: field(name: "status") {
    value
  }
`;

const protocolSitesQuery = `
  query FindProtocolStudy($templateId: String!, $language: String!, $orgStudyId: String!) {
    search(
      where: {
        AND: [
          { name: "_templates", value: $templateId, operator: CONTAINS }
          { name: "_language", value: $language, operator: EQ }
          { name: "protocolId", value: $orgStudyId, operator: EQ }
        ]
      }
      first: 10
    ) {
      results {
        name
        children(first: 1000) {
          results {
            ${SITE_RESULT_FIELDS}
          }
        }
      }
    }
  }
`;

export const getProtocolSitesTemplateId = (value?: string): string => {
  if (!value) {
    return '';
  }

  const trimmed = value.trim();
  const guidPattern =
    /^\{?[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}\}?$/;

  return guidPattern.test(trimmed) ? trimmed.replace(/[{}]/g, '') : '';
};

const getAllowedSiteStatuses = (): string[] => {
  const statuses = process.env.PROTOCOL_SITE_STATUSES || DEFAULT_PROTOCOL_SITE_STATUSES;
  return statuses
    .split(/\s*\|+\s*|\s*,\s*/)
    .map((status) => status.trim())
    .filter(Boolean);
};

const filterSitesByAllowedStatus = (
  siteItems: ProtocolSiteSearchResult[],
  allowedStatuses: string[]
): ProtocolSiteSearchResult[] => {
  const allowed = new Set(allowedStatuses.map((status) => status.toLowerCase()));

  return siteItems.filter((site) => {
    const statusRaw =
      typeof site?.siteStatus === 'string' ? site.siteStatus : site?.siteStatus?.value || '';
    return allowed.has(statusRaw.trim().toLowerCase());
  });
};

export const getStudyLocationsByOrgStudyId = async (
  orgStudyId: string
): Promise<ProtocolSiteSearchResult[]> => {
  if (!orgStudyId) {
    return [];
  }

  const templateId = getProtocolSitesTemplateId(process.env.PROTOCOL_SITES_TEMPLATE_ID);
  const allowedStatuses = getAllowedSiteStatuses();

  if (!templateId || !allowedStatuses.length) {
    return [];
  }

  const response = await getGraphQLClient().request<ProtocolStudySearchResponse>(
    protocolSitesQuery,
    {
      templateId,
      language: process.env.DEFAULT_LANGUAGE || DEFAULT_LANGUAGE,
      orgStudyId,
    }
  );

  const siteItems = (response?.search?.results || []).flatMap(
    (study) => study.children?.results || []
  );

  return filterSitesByAllowedStatus(siteItems, allowedStatuses);
};
